import React, { useState } from 'react';
import { FiX, FiMail, FiSend, FiUser } from 'react-icons/fi';
import { useApplyToService, useUpdateServiceApplication } from '../../../../api/services';
import { useSendMessage } from '../../../../api/conversations';
import { toast } from '../../../../utils/toast';
import { extractApiError } from '../../../../utils/apiError';

interface SendInquiryMailModalProps {
  serviceId: number | string;
  applicationUuid: string;
  guestName: string;
  guestEmail: string;
  serviceTitle: string;
  conversationUuid: string;
  onClose: () => void;
  onSentSuccess?: () => void;
}

export const SendInquiryMailModal: React.FC<SendInquiryMailModalProps> = ({
  serviceId,
  applicationUuid,
  guestName,
  guestEmail,
  serviceTitle,
  conversationUuid,
  onClose,
  onSentSuccess,
}) => {
  const [subject, setSubject] = useState(`Re: Service Inquiry - ${serviceTitle}`);
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<{ subject?: string; message?: string }>({});

  const sendMessageMutation = useSendMessage();
  const { mutateAsync: applyToService, isPending: isApplying } = useApplyToService();
  const { mutateAsync: updateStatus, isPending: isUpdating } = useUpdateServiceApplication();

  const isSending = sendMessageMutation.isPending || isUpdating || isApplying;

  const isValidEmail = (emailStr: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailStr);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: { subject?: string; message?: string } = {};

    // 1. Recipient Email Validation
    if (!guestEmail || !isValidEmail(guestEmail)) {
      toast.error('Recipient email address is missing or invalid.');
      return;
    }

    // 2. Service Reference Validation
    if (!serviceId) {
      toast.error('Service reference is missing. Unable to send inquiry message.');
      return;
    }

    // 3. Subject Validation
    const cleanSubject = subject.trim();
    if (!cleanSubject) {
      newErrors.subject = 'Subject is required.';
    } else if (cleanSubject.length < 3) {
      newErrors.subject = 'Subject must be at least 3 characters long.';
    }

    // 4. Message Body Validation
    const cleanMessage = message.trim();
    if (!cleanMessage) {
      newErrors.message = 'Message is required.';
    } else if (cleanMessage.length < 5) {
      newErrors.message = 'Message body must be at least 5 characters long.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      if (newErrors.subject) {
        toast.error(newErrors.subject);
      } else if (newErrors.message) {
        toast.error(newErrors.message);
      }
      return;
    }

    setErrors({});

    try {
      const formattedMessage = `<strong>Subject: ${cleanSubject}</strong><br><br>${cleanMessage.replace(/\n/g, '<br>')}`;

      // 1. Dispatch the actual email to the recipient's inbox (this is the only endpoint
      // that accepts an arbitrary recipient email rather than the authenticated user's own)
      await applyToService({
        serviceId,
        data: {
          email: guestEmail,
          sender_role: 'organizer',
          message_html: formattedMessage,
          screen_width: window.screen.width,
          screen_height: window.screen.height,
        },
        suppressToast: true,
      });

      // 2. Also drop a copy into the in-app conversation, if one already exists
      if (conversationUuid) {
        await sendMessageMutation.mutateAsync({
          conversationUuid,
          payload: {
            body: `${cleanSubject}\n\n${cleanMessage}`,
            body_html: formattedMessage,
          },
        });
      }

      // 3. Update status to in_conversation (suppress generic toast)
      if (applicationUuid) {
        await updateStatus({
          applicationUuid,
          status: 'in_conversation',
          suppressToast: true,
        });
      }

      // Single consolidated success notification
      toast.success(`Mail sent to ${guestEmail}`);
      if (onSentSuccess) onSentSuccess();
      onClose();
    } catch (err) {
      toast.error(extractApiError(err, 'Failed to send mail'));
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm">
      <div
        className="w-[calc(100vw-32px)] sm:w-full max-w-2xl bg-[#1C1C1E] border border-[#2C2C2E] rounded-[28px] overflow-hidden shadow-2xl max-h-[88vh] flex flex-col"
        style={{ animation: 'fadeInScale 0.2s ease-out' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-[#2C2C2E] shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600/20 text-[#1784FF]">
              <FiMail size={18} />
            </div>
            <div>
              <h3 className="text-white text-base font-bold">Send Mail</h3>
              <p className="text-xs text-zinc-400 truncate max-w-[240px] sm:max-w-sm">
                To {guestName} ({guestEmail})
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-zinc-400 hover:text-white transition-colors rounded-full hover:bg-white/10 cursor-pointer"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Form Body */}
        <form
          onSubmit={handleSend}
          className="p-5 sm:p-6 space-y-4 flex-1 min-h-0 flex flex-col overflow-y-auto custom-modal-scrollbar"
        >
          {/* Recipient info badge */}
          <div className="flex items-center justify-between px-4 py-3 rounded-2xl bg-[#2A2A2D] border border-[#2C2C2E] text-xs shrink-0">
            <div className="flex items-center gap-2 text-zinc-300 min-w-0">
              <FiUser size={14} className="text-blue-400 shrink-0" />
              <span className="font-semibold text-white truncate">{guestName}</span>
              <span className="text-zinc-500 truncate">({guestEmail})</span>
            </div>
            <span className="px-2.5 py-1 rounded-lg bg-blue-500/10 text-blue-400 font-semibold text-[10px] shrink-0 ml-2">
              Service Inquiry
            </span>
          </div>

          {/* Subject Line */}
          <div className="shrink-0">
            <label className="block text-xs font-semibold text-zinc-300 mb-1.5">
              Subject <span className="text-red-400 font-bold">*</span>
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => {
                setSubject(e.target.value);
                if (errors.subject) setErrors((prev) => ({ ...prev, subject: undefined }));
              }}
              placeholder="Email subject..."
              className={`w-full bg-[#2A2A2D] border text-white text-xs rounded-xl px-4 py-3 outline-none transition-all ${
                errors.subject
                  ? 'border-red-500/80 focus:border-red-500'
                  : 'border-[#3A3A3C] focus:border-blue-500'
              }`}
            />
            {errors.subject && (
              <p className="mt-1 text-[11px] font-medium text-red-400">{errors.subject}</p>
            )}
          </div>

          {/* Message Body */}
          <div className="flex-1 min-h-0 flex flex-col">
            <label className="block text-xs font-semibold text-zinc-300 mb-1.5 shrink-0">
              Message <span className="text-red-400 font-bold">*</span>
            </label>
            <textarea
              rows={8}
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                if (errors.message) setErrors((prev) => ({ ...prev, message: undefined }));
              }}
              placeholder="Write your email message here..."
              className={`w-full min-h-[160px] flex-1 bg-[#2A2A2D] border text-white text-xs rounded-xl p-4 outline-none resize-none transition-all custom-modal-scrollbar ${
                errors.message
                  ? 'border-red-500/80 focus:border-red-500'
                  : 'border-[#3A3A3C] focus:border-blue-500'
              }`}
            />
            {errors.message && (
              <p className="mt-1 text-[11px] font-medium text-red-400">{errors.message}</p>
            )}
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-3 pt-2 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 rounded-full border border-white/10 hover:bg-white/5 text-zinc-300 text-xs font-semibold transition-all cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSending}
              className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#1784FF] hover:bg-blue-600 text-white text-xs font-semibold transition-all cursor-pointer shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiSend size={14} />
              <span>{isSending ? 'Sending...' : 'Send Mail'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
